// DATA FRESHNESS strip: one row per report CRM pulls, stating when it came off
// the source system (PULLED), when CRM read it (LOADED) and when each runs
// next. Same wording as sss-inspection's Branch Performance strip.
//
// George 2026-08-26 (Site Access) and 2026-09-14 ("an indicator that tells me
// when the last report was pulled. list all pulled reports"): now page-level on
// Operations with every report, and on the Project Tracker for the RentalMan
// invoices report behind Actual $.
//
// PULLED and LOADED are BOTH shown because they answer different questions and
// can disagree. If a scraper dies, pulled stops moving while loaded keeps
// advancing: CRM re-reads the same old file successfully forever. Showing one
// number is what hides that.
//
// The server owns the verdict (services/siteAccessFeeds.js) so the staleness
// rule lives in one place; this renders it.
import { useEffect, useState } from 'react';
import { styles, colors } from './UI';
import { useRollup, RollupH2 } from './Rollup';

// Clocks carry their zone. The schedule LABELS state each source's own zone
// (the MinisForum tasks fire on Eastern, HASC asked for after-6pm Central)
// while these times render in the VIEWER's zone, so "9:00 PM" beside "daily at
// 8:00 PM CT" reads as a contradiction unless the zone is spelled out.
const fmtWhen = (d) => (d
  ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
  : null);

const FEED_CHIP = {
  current:      { text: 'CURRENT',        bg: '#DCFCE7',        fg: '#15803D' },
  stale:        { text: 'STALE',          bg: colors.amberSoft, fg: '#B45309' },
  failed:       { text: 'LOAD FAILED',    bg: colors.redSoft,   fg: '#991B1B' },
  never:        { text: 'NO DATA YET',    bg: colors.amberSoft, fg: '#B45309' },
  off:          { text: 'AUTO-SYNC OFF',  bg: colors.graySoft,  fg: '#4B5563' },
  unconfigured: { text: 'NOT CONFIGURED', bg: colors.graySoft,  fg: '#4B5563' },
};

const ageText = (h) => (h >= 24
  ? `${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? '' : 's'}`
  : `${Math.round(h)} hours`);

// Why a pull did not land, from the ISN scraper's own run report (server
// utils/isnScrapeStatus.js, 2026-09-29). Null on every other feed, and on ISN
// until the MinisForum runs a scraper that writes one; the chip never moves.
const pullFailed = (s, lead) => `${lead} pull at ${fmtWhen(s.at)} failed while ${s.stage_label}`
  + (s.detail ? `: ${s.detail.replace(/[.!?]+$/, '')}` : '');
const staleWhy = (s) => {
  if (s?.kind === 'failed') return `: ${pullFailed(s, 'the')}`;
  if (s?.kind === 'no_run') return `: no pull has reported since ${fmtWhen(s.at)}, so the scheduled task did not start or stopped before it could report`;
  return ', so the scheduled pull may have missed a run';
};

// Re-read while the page stays open, so "next pull" never sits in the past
// and a report that lands mid-visit shows up without a reload.
const REFRESH_MS = 5 * 60 * 1000;

export default function DataFreshness({ load, rollupId, subtitle, footer }) {
  const [feeds, setFeeds] = useState(null);
  const [collapsed, toggle] = useRollup(rollupId);
  useEffect(() => {
    let alive = true;
    const run = () => load()
      .then(r => { if (alive) setFeeds(r.feeds || []); })
      .catch(() => { if (alive) setFeeds(prev => prev || false); });
    run();
    const t = setInterval(run, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []); // load is a stable api.* reference
  // Loading, or the endpoint is unavailable on this deploy: stay quiet rather
  // than showing an empty box that reads as "no reports".
  if (!feeds || !feeds.length) return null;

  // Rolled up, the strip still answers "when was it pulled" for its first
  // report (RentalMan invoices on both pages) and names the worst problem.
  // A switched-off or unwired feed is not current either.
  const worst = feeds.find(f => f.status === 'failed')
    || feeds.find(f => f.status === 'stale' || f.status === 'never')
    || feeds.find(f => f.status === 'off' || f.status === 'unconfigured');
  const allCurrent = feeds.every(f => f.status === 'current');
  const lead = feeds[0];
  // A row whose status could not be read has no pull time; that is not the
  // same as never pulled, and saying so sends someone after the scraper.
  const noPullText = (f) => (f.status === 'failed' ? 'pull time unavailable' : 'never pulled');
  const leadPulled = fmtWhen(lead.source_modified_at);

  return (
    <div style={{ ...styles.card, borderLeft: `5px solid ${colors.orange}`, background: '#FFF9F5', padding: '12px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: collapsed ? 0 : 8, flexWrap: 'wrap' }}>
        <RollupH2 as="h3" collapsed={collapsed} onToggle={toggle} style={{ fontSize: 12, fontWeight: 800, color: colors.charcoal, letterSpacing: 1 }}>
          🕒 DATA FRESHNESS
        </RollupH2>
        {!collapsed && <span style={{ fontSize: 11.5, color: colors.textMuted }}>{subtitle}</span>}
        {collapsed && (
          <span style={{ fontSize: 12.5, color: colors.textMuted }}>
            {lead.title} {leadPulled ? <>pulled <strong style={{ color: colors.charcoal }}>{leadPulled}</strong></> : noPullText(lead)}
            {allCurrent && feeds.length > 1 ? ` · all ${feeds.length} reports current` : ''}
          </span>
        )}
        {collapsed && worst && (
          <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, letterSpacing: 0.5, background: FEED_CHIP[worst.status].bg, color: FEED_CHIP[worst.status].fg }}>
            {FEED_CHIP[worst.status].text}: {worst.title}
          </span>
        )}
      </div>

      {!collapsed && feeds.map(f => {
        const chip = FEED_CHIP[f.status] || FEED_CHIP.never;
        const pulled = fmtWhen(f.source_modified_at);
        return (
          <div key={f.feed} style={{ marginBottom: 8, fontSize: 13 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '3px 8px' }}>
              <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, letterSpacing: 0.5, background: chip.bg, color: chip.fg }}>
                {chip.text}
              </span>
              <strong style={{ color: colors.charcoal }}>{f.title}</strong>
              {f.view && <span style={{ color: colors.textMuted, fontSize: 12 }}>· {f.view}</span>}
              {f.drives && <span style={{ color: colors.textMuted, fontSize: 12 }}>· drives {f.drives}</span>}
            </div>
            <div style={{ color: colors.textMuted, fontSize: 12.5, marginTop: 2, paddingLeft: 2 }}>
              {pulled
                ? <>{f.api_pull ? 'pulled by CRM ' : `pulled from ${f.source} `}
                    <strong style={{ color: colors.charcoal, fontSize: 13 }}>{pulled}</strong></>
                : f.status === 'failed'
                  ? <span>{noPullText(f)}</span>
                  : <span>never pulled: nothing has been pushed to {f.extra?.file || 'this path'}</span>}
              {!f.api_pull && pulled && (f.loaded_at
                ? <>{' · loaded into this app '}{fmtWhen(f.loaded_at)}</>
                : f.on_demand
                  ? <>{` · ${f.read_label || 'read when a page opens it'}`}</>
                  : <>{' · never loaded into this app'}</>)}
              {f.next_pull_at && <>{' · next pull '}<strong style={{ color: colors.charcoal }}>{fmtWhen(f.next_pull_at)}</strong>{f.pull_label ? ` (${f.pull_label})` : ''}</>}
              {f.api_pull && f.next_read_at && <>{' · next pull '}<strong style={{ color: colors.charcoal }}>{fmtWhen(f.next_read_at)}</strong>{f.read_label ? ` (${f.read_label})` : ''}</>}
              {!f.api_pull && f.next_read_at && <>{' · next CRM read '}{fmtWhen(f.next_read_at)}{f.read_label ? ` (${f.read_label})` : ''}</>}
            </div>
            {f.status === 'stale' && f.age_hours != null && (
              <div style={{ color: '#B45309', fontWeight: 600, fontSize: 12.5, marginTop: 2 }}>
                ⚠ That report is {ageText(f.age_hours)} old{staleWhy(f.last_scrape)}.
                Everything here that uses it is as of {f.last_scrape ? 'the last pull that landed' : 'that pull'}.
              </div>
            )}
            {f.status !== 'stale' && f.last_scrape?.kind === 'failed' && (
              <div style={{ color: '#B45309', fontWeight: 600, fontSize: 12.5, marginTop: 2 }}>
                ⚠ {pullFailed(f.last_scrape, 'The')}.
                {f.status === 'current' ? ' The report above is still the last pull that landed.' : ''}
              </div>
            )}
            {f.status === 'failed' && (
              <div style={{ color: colors.red, fontWeight: 600, fontSize: 12.5, marginTop: 2 }}>
                ⚠ CRM could not load it{f.note ? `: ${/[.!?]$/.test(f.note) ? f.note : `${f.note}.`}` : '.'}
                {f.checked_at ? ` Last tried ${fmtWhen(f.checked_at)}; it` : ' It'} retries automatically on the next read.
              </div>
            )}
            {(f.status === 'off' || f.status === 'unconfigured') && (
              <div style={{ color: '#4B5563', fontSize: 12.5, marginTop: 2 }}>
                {f.status === 'off'
                  ? 'Auto-sync is switched OFF for this feed (Admin sync cards), so nothing refreshes until it is back on.'
                  : 'This feed is not wired up on this deployment, so nothing refreshes it.'}
              </div>
            )}
          </div>
        );
      })}

      {!collapsed && footer && (
        <div style={{ color: colors.textMuted, fontSize: 11.5, borderTop: `1px solid ${colors.border}`, paddingTop: 6, marginTop: 2 }}>
          {footer}
        </div>
      )}
    </div>
  );
}
